import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import UniversalRouter from 'universal-router';
import generateUrls from 'universal-router/generateUrls';
import { changePage } from './redux-router';
var STORE_KEY = Symbol('store');

function normalizeData(data) {
  if (typeof data === 'string') {
    return {
      name: data
    };
  }

  return data;
}

function getUrlDepth(url) {
  return url.replace(/\/$/, '').split('/').length;
}

function compareUrlDepth(urlA, urlB) {
  return getUrlDepth(urlB) - getUrlDepth(urlA);
}
/**
 * Enriches request with some data. Format of data is inspired by ring spec: https://github.com/ring-clojure/ring/blob/master/SPEC
 */


function enrichRequestWithQueryString(request, context) {
  if (context.queryString != null && context.queryString !== '') {
    return Object.assign({}, request, {
      queryString: context.queryString
    });
  }

  return request;
}

function enrichRequestWithStore(store) {
  if (store == null) {
    return;
  }

  return function (request) {
    return Object.assign({}, request, _defineProperty({}, STORE_KEY, store));
  };
}

function createRequestEnhancer() {
  for (var _len = arguments.length, enhancers = new Array(_len), _key = 0; _key < _len; _key++) {
    enhancers[_key] = arguments[_key];
  }

  var enhs = enhancers.filter(function (enh) {
    return enh != null;
  });
  return function (request, context) {
    return enhs.reduce(function (req, enhancer) {
      return enhancer(req, context);
    }, request);
  };
}

function fromObjectRoutes(objRoutes) {
  var orderedPaths = Object.keys(objRoutes).sort(compareUrlDepth);
  return orderedPaths.map(function (path) {
    var data = normalizeData(objRoutes[path]);
    return {
      path: path,
      name: data.name,
      children: data.children ? fromObjectRoutes(data.children) : [],
      action: function action(context) {
        var _context$params;

        return {
          context: context,
          match: {
            data: data,
            pathParams: (_context$params = context.params) !== null && _context$params !== void 0 ? _context$params : {}
          }
        };
      }
    };
  });
}

function currentLoc() {
  return typeof location === 'undefined' ? '' : location.href;
}

function createUrl(input, base) {
  if (typeof URL !== 'undefined') {
    return new URL(input, base);
  }

  if (typeof require !== 'undefined') {
    var url = require('url');

    return new url.URL(input, base);
  }
}

function parseUrl(url) {
  if (url == null) {
    return url;
  }

  var u = createUrl(url, 'http://example.com');
  return {
    pathname: u.pathname,
    queryString: u.searchParams.toString()
  };
}

function defaultOnChange(request) {
  var handler = request.match.data.handler;

  if (handler != null) {
    handler(request);
  }
}

function requestToPage(request) {
  if (request.match == null) {
    return null;
  }

  return {
    name: request.match.data.name,
    params: Object.fromEntries(Object.entries({
      path: request.match.pathParams,
      queryString: request.queryString
    }).filter(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
          v = _ref2[1];

      return v != null;
    }))
  };
}
/**
 *
 * @param {Object} request
 * @param {bool} dispatchChange Whether dispatch change to router store
 */


function defaultOnChangeWithRedux(request, dispatchChange) {
  var page = requestToPage(request);

  if (dispatchChange === true) {
    request[STORE_KEY].dispatch(changePage(page === null || page === void 0 ? void 0 : page.name, page === null || page === void 0 ? void 0 : page.params));
  }

  defaultOnChange(request);
}
/**
 * @param {Object} options
 * @param {Object} options.routes Keys are paths in format `/path/:param`, values are route data
 *     (object with key 'name' or string)
 * @param {Function=} options.onChange Function accepting request called when route is matched
 * @param {Function=} options.notFoundHandler Function accepting request called when no route is matched
 * @param {Function=} options.navHandler Function called instead of `nav` and `redirect` (useful for SSR)
 * @param {string=} options.currentUrl Useful when doing SSR
 * @param {store=} options.store Redux store to which current page will be stored
 * @param {Object=} options.generateUrlsOptions Options with params for generateUrls https://github.com/kriasoft/universal-router/blob/main/docs/api.md#url-generation
 *
 * Request is map with optional keys:
 * - `match`
 *   - matched route. It is object with keys `data` (route data object), `pathParams`
 * - `queryString`
 */


export function create(_ref3) {
  var routes = _ref3.routes,
      onChange = _ref3.onChange,
      notFoundHandler = _ref3.notFoundHandler,
      currentUrl = _ref3.currentUrl,
      navHandler = _ref3.navHandler,
      store = _ref3.store,
      generateUrlsOptions = _ref3.generateUrlsOptions;

  if (onChange == null) {
    onChange = store == null ? defaultOnChange : defaultOnChangeWithRedux;
  }

  var enrichRequest = createRequestEnhancer(enrichRequestWithQueryString, enrichRequestWithStore(store));
  var universalRoutes = fromObjectRoutes(routes);
  var options = {
    resolveRoute: function resolveRoute(context, params) {
      if (typeof context.route.action === 'function') {
        var request = context.route.action(context, params); //
        // We want to dispatch change route on every last route.
        // If dispatchChange would be true, then change is dispatched on each route child.
        // context.path === '' is case when is changing query string otherwise we check if
        //  context.baseUrl + context.path is same like context.pathname => if so, then
        // the we are on last child
        //

        var dispatchChange = context.path !== '' ? "".concat(context.baseUrl).concat(context.path) === context.pathname : true;
        onChange(enrichRequest(request, context), dispatchChange);
        return true;
      }
    },
    errorHandler: function errorHandler(error, context) {
      if (error.status === 404 && notFoundHandler) {
        notFoundHandler(enrichRequest({}, context));
        return true;
      }

      console.error(error);
      return true;
    }
  };
  var baseRouter = new UniversalRouter(universalRoutes, options);
  var url = generateUrls(baseRouter, generateUrlsOptions);
  baseRouter.resolve(parseUrl(currentUrl !== null && currentUrl !== void 0 ? currentUrl : currentLoc()));

  var urlChanged = function urlChanged() {
    baseRouter.resolve(parseUrl(currentLoc()));
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', urlChanged);
  }

  var router = {
    nav: function nav(url) {
      history.pushState({}, '', url);
      urlChanged();
    },
    redirect: function redirect(url) {
      history.replaceState({}, '', url);
      urlChanged();
    },
    refresh: function refresh() {
      baseRouter.resolve(parseUrl(currentLoc()));
    },
    pathFor: function pathFor(page, params) {
      return url(page, params);
    },
    destroy: function destroy() {
      if (typeof window !== 'undefined') {
        window.removeEventListener('popstate', urlChanged);
      }
    }
  };
  return Object.assign({}, router, navHandler ? {
    nav: function nav(url) {
      navHandler(url);
    },
    redirect: function redirect(url) {
      navHandler(url);
    }
  } : {});
}